import React from "react";
import { Grid, LinearProgress, linearProgressClasses, Skeleton, Stack } from "@mui/material";
import styled from "styled-components";
import BadgeFull from "src/scenes/achievements/badge-full";
import request from "src/utils/request";
import { format } from "date-fns";
import { Helmet } from "react-helmet";
import { useAuth } from "../../components/providers/auth-context";
import Portal from "../../components/portal";
import DemoBarDemo from "../../components/demo-bar-demo";

const H1 = styled.h1`
    font-size: 18px;
    margin: 0;
    font-weight: normal;
    color: ${({ theme }) => theme.palette.text.secondary};
`;

const BadgesCounterLetters = styled.p`
    font-size: 42px;
    margin: 0;
`;

const BadgesCollectedLetters = styled.p`
    font-size: 12px;
    font-weight: 300;
    padding-bottom: 8px;
    margin: 0;
`;

const CategoryTitleLetters = styled.h2`
    font-size: 14px;
    font-weight: 300;
    margin-top: 24px;
    margin-bottom: 24px;
    margin-left: 8px;
`;

const BorderLinearProgress = styled(LinearProgress)(({ theme }) => ({
    height: 10,
    borderRadius: 5,
    [`&.${linearProgressClasses.colorPrimary}`]: {
        backgroundColor: theme.palette.grey[theme.palette.mode === "light" ? 200 : 800],
    },
    [`& .${linearProgressClasses.bar}`]: {
        borderRadius: 5,
        backgroundColor: theme.palette.mode === "dark" ? "#59C36A" : "#308fe8",
    },
}));

const Achievements = () => {
    const [achievements, setAchievements] = React.useState([]);
    const { isDemo } = useAuth();

    const getEndpointAchievements = async () => {
        let response;
        if (isDemo) {
            response = (await import("./mock-achievements")).default;
        } else {
            response = await request(`api/users/me/achievements`, { method: "GET" });
        }
        setAchievements(response);
    };
    React.useEffect(() => {
        getEndpointAchievements();
    }, [isDemo]);

    const IntroductoryAchievements =
        achievements && achievements.achievements && achievements.achievements.introductory;
    const BasicAchievements =
        achievements && achievements.achievements && achievements.achievements.basic;
    const TeamAchievements =
        achievements && achievements.achievements && achievements.achievements.team;
    const WorkAchievements =
        achievements && achievements.achievements && achievements.achievements.work;
    const CollaborationAchievements =
        achievements && achievements.achievements && achievements.achievements.collaboration;
    const WellbeingAchievements =
        achievements && achievements.achievements && achievements.achievements.wellbeing;
    const EngagementAchievements =
        achievements && achievements.achievements && achievements.achievements.engagement;
    const MilestoneAchievements =
        achievements && achievements.achievements && achievements.achievements.milestone;
    const AchievementStats = achievements.achievementStats;

    return (
        <>
            <Helmet>
                <title>Adadot - Your Achievements</title>
            </Helmet>
            <Portal>
                <DemoBarDemo />
            </Portal>
            <Stack
                direction="row"
                pt={4}
                justifyContent="space-between"
                alignItems="center"
                style={{ width: "100%" }}
            >
                <Stack direction="column">
                    <H1>Achievements & badges</H1>
                    {achievements && AchievementStats && (
                        <BadgesCollectedLetters>
                            {AchievementStats.latest
                                ? `Last collected on ${format(
                                      new Date(AchievementStats.latest * 1000),
                                      "PPP"
                                  )}`
                                : ""}
                        </BadgesCollectedLetters>
                    )}
                </Stack>
                <Stack direction="column">
                    <Stack direction="column" alignItems="center">
                        {achievements && AchievementStats ? (
                            <BadgesCounterLetters>
                                {AchievementStats.completed}/{AchievementStats.total}
                            </BadgesCounterLetters>
                        ) : (
                            <Stack direction="row" spacing={1}>
                                <Skeleton variant="text" sx={{ fontSize: "42px", width: "24px" }} />
                                <BadgesCounterLetters>/</BadgesCounterLetters>
                                <Skeleton variant="text" sx={{ fontSize: "42px", width: "24px" }} />
                            </Stack>
                        )}
                        <BadgesCollectedLetters>Badges collected</BadgesCollectedLetters>
                    </Stack>
                    {AchievementStats && (
                        <BorderLinearProgress
                            variant="determinate"
                            value={(AchievementStats.completed / AchievementStats.total) * 100}
                        />
                    )}
                </Stack>
            </Stack>
            {achievements && achievements.length !== 0 && (
                <Grid container alignItems="left" pt={2} mb={4}>
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Introductory
                    </CategoryTitleLetters>
                    {IntroductoryAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Engagement
                    </CategoryTitleLetters>
                    {EngagementAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Milestones
                    </CategoryTitleLetters>
                    {MilestoneAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Basic
                    </CategoryTitleLetters>
                    {BasicAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Team
                    </CategoryTitleLetters>
                    {TeamAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Work
                    </CategoryTitleLetters>
                    {WorkAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Collaboration
                    </CategoryTitleLetters>
                    {CollaborationAchievements.map((achievement) => BadgeFull({ achievement }))}
                    <CategoryTitleLetters style={{ width: "100%", paddingBottom: "16px" }}>
                        Wellbeing
                    </CategoryTitleLetters>
                    {WellbeingAchievements.map((achievement) => BadgeFull({ achievement }))}
                </Grid>
            )}
        </>
    );
};
export default Achievements;
